import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { loadFeatureAccess } from 'mojo-feature-access';
import { MojoFeatureExtensionsService } from 'mojo-feature-extensions';
import { MojoPrivilegesService } from 'mojo-privileges';
import { Observable, of } from 'rxjs';
import { catchError, map, mergeMap, pluck, switchMap, withLatestFrom } from 'rxjs/operators';

import { AppFeatureExtensions } from '@app/app-feature-extensions.config';
import { notificationsUpdateAnonymizedNames } from '@collections/notifications/notifications.actions';
import { licenseLoadExtensions } from '@core/settings/license-configuration/store/license-configuration.actions';
import { AlertActionsUnion, DangerAlert, SuccessAlert } from '@shared/alert';
import { User } from '@shared/models';
import { Pagination } from '@shared/pagination';
import { UserService } from '@shared/services';

import {
  selfUpdateProfile,
  UserActionsUnion,
  UsersActionTypes,
  UsersChangePasswordFailure,
  UsersChangePasswordSuccess,
  UsersCreateFailure,
  UsersCreateSuccess,
  UsersGet,
  UsersGetSuccess,
  UsersListFailure,
  UsersListSuccess,
  UsersLoad,
  UsersLoadSuccess,
  UsersLoggedInSuccess,
  UsersUpdateFailure,
  UsersUpdateLoggedInUserSuccess,
  UsersUpdateSuccess,
} from './users.actions';
import { getUsersLoggedIn } from './users.selectors';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
// Use for local privileges mock
const LOCAL_PRIVILEGES = [];

@Injectable()
export class UsersEffects {
  loadAll$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActionTypes.Load),
      map((action: UsersLoad) => action.payload),
      switchMap((payload) =>
        this.userService
          .get(payload)
          .pipe(map((usersPage: Pagination<User>) => new UsersLoadSuccess(usersPage))),
      ),
    ),
  );

  load$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActionTypes.Get),
      map((action: UsersGet) => action.payload),
      mergeMap((payload) =>
        this.userService
          .get({ id: payload.id, params: { locked: payload.locked } })
          .pipe(map((user: User) => new UsersGetSuccess(user))),
      ),
    ),
  );

  loadList$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActionTypes.List),
      pluck('payload', 'userIds'),
      mergeMap((usersIds) =>
        this.userService.getList(usersIds).pipe(
          map((users: User[]) => new UsersListSuccess({ users })),
          catchError((error: any) => of(new UsersListFailure({ error }))),
        ),
      ),
    ),
  );

  loggedIn$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActionTypes.LoggedIn),
      map((action: UsersGet) => action.payload),
      switchMap(() =>
        this.userService.getProfile().pipe(map((user: User) => new UsersLoggedInSuccess(user))),
      ),
    ),
  );

  create$: Observable<UserActionsUnion> = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActionTypes.Create),
      pluck('payload'),
      withLatestFrom(
        this.featureExtensionService.hasFeatureExtension(AppFeatureExtensions.SAML_AUTHENTICATION),
      ),
      switchMap(([user, samlAuthenticationActive]: [User, boolean]) =>
        this.userService
          .post({ body: { ...user, skipAuth0Creation: samlAuthenticationActive } })
          .pipe(
            map((response: User) => new UsersCreateSuccess(response)),
            catchError((error) => of(new UsersCreateFailure({ error }))),
          ),
      ),
    ),
  );

  createSuccessAlert$: Observable<AlertActionsUnion> = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActionTypes.CreateSuccess),
      map(() => new SuccessAlert('An email invitation has been sent')),
    ),
  );

  loggedInSuccess: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActionTypes.LoggedInSuccess),
      pluck('payload'),
      mergeMap((user: User) => {
        this.privilegesService.setUserPrivileges([...user.resources, ...LOCAL_PRIVILEGES]);
        return [licenseLoadExtensions(), loadFeatureAccess()];
      }),
    ),
  );

  createSuccessReload$: Observable<UsersLoad> = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActionTypes.CreateSuccess),
      map(() => new UsersLoad({ params: { offset: 0 } })),
    ),
  );

  update$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActionTypes.Update),
      pluck('payload'),
      switchMap(({ id, body }: { id: number; body: User }) =>
        this.userService.patch({ id, body }).pipe(
          withLatestFrom(this.store.select(getUsersLoggedIn)),
          mergeMap(([updatedUser, loggedInUser]: [User, User]) => {
            if (updatedUser.id === loggedInUser.id) {
              return [
                new UsersUpdateLoggedInUserSuccess(updatedUser),
                notificationsUpdateAnonymizedNames({ isAnonymized: updatedUser.anonymization }),
              ];
            }
            return [
              new UsersUpdateSuccess(updatedUser),
              notificationsUpdateAnonymizedNames({ isAnonymized: updatedUser.anonymization }),
            ];
          }),
          catchError((error) => of(new UsersUpdateFailure({ error }))),
        ),
      ),
    ),
  );

  updateSuccess$: Observable<AlertActionsUnion> = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActionTypes.UpdateSuccess, UsersActionTypes.UpdateLoggedInUserSuccess),
      map(() => new SuccessAlert('Your edits have been saved')),
    ),
  );

  updateFail$: Observable<AlertActionsUnion> = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActionTypes.UpdateFailure),
      map(() => new DangerAlert('There was a problem with user update')),
    ),
  );

  createFail$: Observable<AlertActionsUnion> = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActionTypes.CreateFailure),
      pluck('payload'),
      map((payload: ErrorEvent) =>
        payload?.error?.error.includes('Auth0 connection error')
          ? new DangerAlert('User with this email already exists in the system')
          : new DangerAlert('There was a problem with user create'),
      ),
    ),
  );

  changePassword$: Observable<UserActionsUnion> = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActionTypes.ChangePassword),
      pluck('payload'),
      switchMap((body) =>
        this.userService.changePassword({ body }).pipe(
          map((user: User) => new UsersChangePasswordSuccess(user)),
          catchError((error) => of(new UsersChangePasswordFailure({ error }))),
        ),
      ),
    ),
  );

  changePasswordSuccess$: Observable<AlertActionsUnion> = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActionTypes.ChangePasswordSuccess),
      map(() => new SuccessAlert('Your edits have been saved')),
    ),
  );

  changePasswordFail$: Observable<AlertActionsUnion> = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActionTypes.ChangePasswordFailure),
      pluck('payload'),
      map((error: HttpErrorResponse) =>
        error?.error?.status === HttpStatusCode.BadRequest
          ? new DangerAlert('Given current password is incorrect')
          : new DangerAlert('There was a problem with user update'),
      ),
    ),
  );

  loadCities$ = createEffect(() =>
    this.actions$.pipe(
      ofType(selfUpdateProfile),
      switchMap((payload) =>
        this.userService.patch({ url: 'self-update', body: payload.body }).pipe(
          map((updatedUser: User) => new UsersUpdateLoggedInUserSuccess(updatedUser)),
          catchError((error) => of(new UsersUpdateFailure({ error }))),
        ),
      ),
    ),
  );

  constructor(
    private actions$: Actions,
    private userService: UserService,
    private privilegesService: MojoPrivilegesService,
    private featureExtensionService: MojoFeatureExtensionsService,
    private store: Store<{}>,
  ) {}
}
